import React, { useState } from 'react';
import { validateVoucher, validateCoupon, applyVoucher, applyCoupon } from '../lib/discounts';
import { useAuth } from '../AuthContext';
import { ToastContext } from './ToastProvider';

const DiscountInput = ({ subtotal, onDiscountApplied, appliedDiscounts = [] }) => {
  const [discountCode, setDiscountCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { user } = useAuth();
  const { notify } = React.useContext(ToastContext);

  const handleDiscountSubmit = async () => {
    if (!discountCode.trim()) return;

    // Check if any discount is already applied
    if (appliedDiscounts.length > 0) {
      setError('Only one discount can be applied per order. Remove the existing discount first.');
      return;
    }

    setIsValidating(true);
    setError('');
    setSuccess('');

    try {
      // Try voucher first
      let result = await validateVoucher(discountCode.trim(), user.uid, subtotal);
      
      if (result.valid) {
        // Apply voucher
        const applyResult = await applyVoucher(result.voucher.id, user.uid, 'temp');
        
        if (applyResult.success) {
          setSuccess('Voucher applied successfully!');
          setDiscountCode('');
          
          // Notify parent component
          onDiscountApplied({
            type: 'voucher',
            code: result.voucher.code,
            description: result.voucher.description,
            discountAmount: result.discount,
            discountType: result.discountType,
            voucherId: result.voucher.id
          });

          notify('Voucher applied successfully!', { type: 'success' });
        } else {
          setError(applyResult.error || 'Failed to apply voucher');
        }
        return;
      }

      // If not a voucher, try coupon
      result = await validateCoupon(discountCode.trim(), user.uid, subtotal);
      
      if (result.valid) {
        // Apply coupon
        const applyResult = await applyCoupon(result.coupon.id, user.uid, 'temp');
        
        if (applyResult.success) {
          setSuccess('Coupon applied successfully!');
          setDiscountCode('');
          
          // Notify parent component
          onDiscountApplied({
            type: 'coupon',
            code: result.coupon.code,
            description: result.coupon.description,
            discountAmount: result.discount,
            discountType: result.discountType,
            couponId: result.coupon.id
          });

          notify('Coupon applied successfully!', { type: 'success' });
        } else {
          setError(applyResult.error || 'Failed to apply coupon');
        }
      } else {
        setError('Invalid voucher or coupon code');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setError('Error applying discount');
    } finally {
      setIsValidating(false);
    }
  };

  const removeDiscount = (discountToRemove) => {
    onDiscountApplied(discountToRemove, true); // true indicates removal
  };

  return (
    <div className="space-y-4">
      {/* Applied Discount */}
      {appliedDiscounts.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-800 mb-2">Applied Discount</h3>
          <div className="space-y-2">
            {appliedDiscounts.map((discount, index) => (
              <div key={index} className="flex items-center justify-between bg-white rounded px-3 py-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {discount.code} - {discount.description}
                  </div>
                  <div className="text-sm text-gray-500">
                    {discount.discountType === 'percentage' && `${discount.discountAmount}% off`}
                    {discount.discountType === 'fixed' && `₱${discount.discountAmount} off`}
                    {discount.discountType === 'free_shipping' && 'Free shipping'}
                  </div>
                </div>
                <button
                  onClick={() => removeDiscount(discount)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Single Discount Input */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Have a Voucher or Coupon?</h3>
        <div className="space-y-3">
          <div className="flex space-x-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="Enter voucher or coupon code"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isValidating || appliedDiscounts.length > 0}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleDiscountSubmit();
                }
              }}
            />
            <button
              type="button"
              onClick={handleDiscountSubmit}
              disabled={!discountCode.trim() || isValidating || appliedDiscounts.length > 0}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Applying...' : 'Apply'}
            </button>
          </div>
          
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {error}
            </div>
          )}
          
          {success && (
            <div className="text-sm text-green-600 bg-green-50 px-3 py-2 rounded">
              {success}
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="text-xs text-gray-500 text-center">
        <p>Enter your voucher or coupon code above. Only one discount can be applied per order.</p>
        <p>Delivery vouchers are automatically applied if eligible.</p>
      </div>
    </div>
  );
};

export default DiscountInput;
